import pako from 'pako';

const DB_NAME = 'pinnacle-geo';
const STORE_NAME = 'polygons';
const DB_VERSION = 1;
const CURRENT_POLYGON_ID = 'polygon-current';

let dbInstance = null;

/**
 * Initialize IndexedDB connection
 */
function openDB() {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Save compressed GeoJSON string to IndexedDB
 * Always overwrites the current polygon (only one active polygon at a time)
 * @param {string} data - GeoJSON string to compress and store
 * @returns {Promise<string>} - Returns the ID of the stored polygon
 */
export async function savePolygonString(data) {
  if (!data || typeof data !== 'string') {
    throw new Error('Invalid data: must be a non-empty string');
  }

  const db = await openDB();

  try {
    // Compress the data
    const compressed = pako.deflate(data);

    // Convert to base64 for storage - avoid apply() with large arrays
    let binaryString = '';
    for (let i = 0; i < compressed.length; i++) {
      binaryString += String.fromCharCode(compressed[i]);
    }
    const base64 = btoa(binaryString);

    const entry = {
      id: CURRENT_POLYGON_ID,
      data: base64,
      originalSize: data.length,
      compressedSize: base64.length,
      timestamp: Date.now(),
      format: 'gzip-base64'
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(entry); // put() overwrites if exists

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(CURRENT_POLYGON_ID);
    });
  } catch (error) {
    console.error('Error saving polygon string:', error);
    throw error;
  }
}

/**
 * Retrieve and decompress GeoJSON string from IndexedDB
 * @returns {Promise<string|null>} - Decompressed GeoJSON string, or null if not found
 */
export async function getPolygonString() {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(CURRENT_POLYGON_ID);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const entry = request.result;
      if (!entry) {
        resolve(null);
        return;
      }

      try {
        // Decode from base64
        const binaryString = atob(entry.data);
        const compressed = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          compressed[i] = binaryString.charCodeAt(i);
        }

        // Decompress
        const decompressed = pako.inflate(compressed, { to: 'string' });
        resolve(decompressed);
      } catch (error) {
        reject(new Error(`Error decompressing polygon: ${error.message}`));
      }
    };
  });
}

/**
 * Delete the current stored polygon
 * @returns {Promise<void>}
 */
export async function deletePolygonString() {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(CURRENT_POLYGON_ID);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get metadata about the current stored polygon (size info, timestamp)
 * @returns {Promise<Object|null>} - Metadata object, or null if not found
 */
export async function getPolygonMetadata() {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(CURRENT_POLYGON_ID);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const entry = request.result;
      if (!entry) {
        resolve(null);
        return;
      }

      resolve({
        id: entry.id,
        originalSize: entry.originalSize,
        compressedSize: entry.compressedSize,
        compressionRatio: ((1 - entry.compressedSize / entry.originalSize) * 100).toFixed(2) + '%',
        timestamp: entry.timestamp
      });
    };
  });
}

/**
 * Clear all stored polygons (cleanup)
 * @returns {Promise<void>}
 */
export async function clearAllPolygons() {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export default {
  savePolygonString,
  getPolygonString,
  deletePolygonString,
  getPolygonMetadata,
  clearAllPolygons
};
